import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import AppTableContainer from "@crema/core/AppTableContainer";
import { PolygonData } from "types/models/master/MasterApp";
import TableHeading from "./TableHeading";
import TableItem from "./TableItem";

interface PolygonTableProps {
  polygon: PolygonData[];
}

type Order = "asc" | "desc";

const PolygonTable: React.FC<PolygonTableProps> = ({ polygon }) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof PolygonData>("id");

  if (!polygon || polygon.length === 0) {
    return <p>No data available</p>;
  }

  const handleSort = (property: keyof PolygonData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const stableSort = (
    array: PolygonData[],
    comparator: (a: PolygonData, b: PolygonData) => number
  ) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [PolygonData, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order: Order, orderBy: keyof PolygonData) => {
    return order === "desc"
      ? (a: PolygonData, b: PolygonData) => descendingComparator(a, b, orderBy)
      : (a: PolygonData, b: PolygonData) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a: PolygonData, b: PolygonData, orderBy: keyof PolygonData) => {
    if (a[orderBy] === null || a[orderBy] === undefined) return 1;
    if (b[orderBy] === null || b[orderBy] === undefined) return -1;

    if (typeof a[orderBy] === "string" && typeof b[orderBy] === "string") {
      return b[orderBy].localeCompare(a[orderBy]);
    }
    if (a[orderBy] < b[orderBy]) {
      return -1;
    }
    if (a[orderBy] > b[orderBy]) {
      return 1;
    }
    return 0;
  };

  const sortedData = stableSort(polygon, getComparator(order, orderBy));

  return (
    <AppTableContainer>
      <Table
        stickyHeader
        className="table"
        sx={{
          tableLayout: "fixed", 
          width: "100%",
        }}
      >
        <TableHead>
          <TableHeading orderBy={orderBy} order={order} onSort={handleSort} />
        </TableHead>
        <TableBody>
          {sortedData.map((data) => (
            <TableItem data={data} key={data.id} />
          ))}
        </TableBody>
      </Table>
    </AppTableContainer>
  );
};

export default PolygonTable;
